export const DEFAULT_KEY_TEXT = ''
export const SHARE_BASE_URL = 'https://mysweety.life'
export const WHITELABEL_ROOM_BASE_URL = 'https://mysweety.online'
export const STRIPCHAT_PLAYER_URL = 'https://creative.whitetrafsa.com/widgets/Player'

export function buildStripchatPlayerUrl({
  userId,
  modelName,
  posterUrl,
  targetDomain,
  autoplay = 'all',
  clickThroughUrl = false,
  nonStopPlaying = true,
  volumeControl = true,
  fullscreen = true,
  tracking = {},
} = {}) {
  const normalizedUserId = (userId || '').toString().trim()
  const normalizedModelName = (modelName || '').toString().trim()
  if (!normalizedUserId || !normalizedModelName) return null

  const params = new URLSearchParams({
    userId: normalizedUserId,
    modelName: normalizedModelName,
    strict: '1',
    thumbFit: 'cover',
    autoplay,
    clickThroughUrl: clickThroughUrl ? '1' : '0',
    nonStopPlaying: nonStopPlaying ? '1' : '0',
    volumeControl: volumeControl ? '1' : '0',
    fullscreen: fullscreen ? '1' : '0',
  })

  const normalizedPosterUrl = (posterUrl || '').toString().trim()
  if (normalizedPosterUrl) {
    params.set('posterUrl', normalizedPosterUrl)
  }

  const normalizedTargetDomain = normalizeTargetDomain(targetDomain)
  if (normalizedTargetDomain) {
    params.set('targetDomain', normalizedTargetDomain)
  }

  for (const key of ['campaignId', 'sourceId', 'p1', 'p2', 'p3']) {
    const value = tracking?.[key]
    if (value !== null && value !== undefined && String(value).trim()) {
      params.set(key, String(value).trim())
    }
  }

  return `${STRIPCHAT_PLAYER_URL}?${params.toString()}`
}

function normalizeTargetDomain(value) {
  const normalized = (value || '').toString().trim()
  if (!normalized) return ''
  try {
    return new URL(normalized.includes('://') ? normalized : `https://${normalized}`).hostname
  } catch {
    return ''
  }
}

export function buildStripchatChatParams({
  userId,
  modelName,
  targetDomain,
  tracking = {},
} = {}) {
  const normalizedUserId = (userId || '').toString().trim()
  const normalizedModelName = (modelName || '').toString().trim()
  if (!normalizedUserId || !normalizedModelName) return null

  const params = {
    userId: normalizedUserId,
    modelName: normalizedModelName,
    strict: 1,
    theme: 'light',
    messagesLimit: 20,
    reversed: 0,
    sound: 'off',
  }

  const normalizedTargetDomain = normalizeTargetDomain(targetDomain)

  if (normalizedTargetDomain) {
    params.targetDomain = normalizedTargetDomain
  }

  for (const key of ['campaignId', 'creativeId', 'sourceId', 'p1', 'p2', 'p3']) {
    const value = tracking?.[key]
    if (value !== null && value !== undefined && String(value).trim()) {
      params[key] = String(value).trim()
    }
  }

  return params
}

export function buildKeyText(...candidates) {
  const source = candidates
    .map((value) => (value || '').toString().trim())
    .find(Boolean) || DEFAULT_KEY_TEXT

  return source.length > 64 ? source.slice(0, 64) : source
}

export function resolveVideosList(gender) {
  const normalized = (gender || '').toLowerCase()

  switch (normalized) {
    case 'male':
      return '003_gay'
    case 'couples':
      return 'evaelfie'
    case 'trans':
      return 'cum69x'
    default:
      return 'kairakampen_preroll'
  }
}

export function normalizeContentType(value) {
  return (value || '').toString().trim().toLowerCase()
}

export function normalizeCount(value) {
  if (value === null || value === undefined) return null

  const numeric = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(numeric) ? numeric : null
}

export function getContentKind(item, fallbackKind = 'photo') {
  const normalizedType = normalizeContentType(item?.type || item?.source)
  if (normalizedType === 'video') return 'video'
  if (normalizedType === 'photo' || normalizedType === 'image' || normalizedType === 'img') return 'photo'
  return fallbackKind
}

export function getContentItemPreviewUrl(item, kind = 'photo') {
  if (kind === 'video') {
    const thumbnailUrl = item?.thumbnail_url ?? item?.thumbnailUrl
    return getImageUrl(thumbnailUrl)
  }

  return getImageUrl(item?.url)
}

// Keep partner URLs and signatures intact; images load directly from the CDN.
export function getImageUrl(value) {
  return (value || '').toString().trim() || '/placeholder.svg'
}

export function getContentItemLabel(item, kind = 'photo', index = 0) {
  const fallbackIndexLabel = kind === 'video' ? `Video ${index + 1}` : `Photo ${index + 1}`
  const fallbackIdLabel = item?.id ? `${kind === 'video' ? 'Video' : 'Photo'} #${item.id}` : null

  return item?.title || item?.description || fallbackIdLabel || fallbackIndexLabel
}

export function buildViewerItems(items = [], kind = 'photo') {
  return items
    .map((item, index) => {
      const url = item?.url || null
      if (!url) return null

      const itemKind = getContentKind(item, kind)
      return {
        kind: itemKind,
        url,
        label: getContentItemLabel(item, itemKind, index),
        previewUrl: getContentItemPreviewUrl(item, itemKind),
      }
    })
    .filter(Boolean)
}

export function getNextViewerIndex(currentIndex, total, direction) {
  if (!Number.isFinite(total) || total <= 0) return 0
  const delta = direction === 'prev' ? -1 : 1
  return (currentIndex + delta + total) % total
}

// The source feed stores male tags as men/*, while the public landing route is
// /boys/*. Map a category route back to the exact source tag.
const sourceTagPrefixes = { boys: 'men' }

export function tagFromRoute(prefix, slug) {
  return `${sourceTagPrefixes[prefix] || prefix}/${slug}`
}

export function tagToRoute(tag) {
  const normalized = (tag || '').toString().trim().replace(/^#/, '')
  if (!normalized) return '/tags'

  const parts = normalized.split('/').filter(Boolean)
  if (parts.length >= 2) {
    const prefix = parts[0].toLowerCase()
    const rest = encodeURIComponent(parts.slice(1).join('/'))

    switch (prefix) {
      case 'girls':
        return `/girls/${rest}`
      case 'boys':
      case 'guys':
      case 'men':
        return `/boys/${rest}`
      case 'couples':
      case 'couple':
        return `/couples/${rest}`
      case 'trans':
        return `/trans/${rest}`
      default:
        return `/tag/${encodeURIComponent(normalized)}`
    }
  }

  return `/tag/${encodeURIComponent(normalized)}`
}

export function buildFavoritesHref(pubCfg, roomName, favoritedCount) {
  const whitelabelGoBaseUrl = pubCfg?.whitelabel_go_base_url?.replace(/\/$/, '')
  const whitelabelBaseUrl = pubCfg?.whitelabel_base_url
    ? pubCfg.whitelabel_base_url.replace(/\/$/, '').replace(/^https?:\/\//, '')
    : ''
  const userId = pubCfg?.stripcash_user_id

  if (!whitelabelGoBaseUrl || !whitelabelBaseUrl || !userId || !roomName) {
    return null
  }

  return `${whitelabelGoBaseUrl}?campaignId=${favoritedCount}&targetDomain=${normalizeTargetDomain(WHITELABEL_ROOM_BASE_URL)}&onlineModels=${encodeURIComponent(roomName)}&action=showTokensGiveawayModalDirectLink&sound=on&userId=${userId}`
}

export function buildRoomProfileHref(userId) {
  const normalized = (userId || '').toString().trim()
  if (!normalized) return null
  return `${SHARE_BASE_URL}/${encodeURIComponent(normalized)}`
}

export function buildWhitelabelRoomHref(roomSlug, baseUrl = WHITELABEL_ROOM_BASE_URL) {
  const normalizedSlug = (roomSlug || '').toString().trim()
  const normalizedBaseUrl = (baseUrl || WHITELABEL_ROOM_BASE_URL).toString().trim().replace(/\/$/, '')
  if (!normalizedSlug || !normalizedBaseUrl) return null
  return `${normalizedBaseUrl}/${encodeURIComponent(normalizedSlug)}`
}

export function buildCreateAccountHref() {
  return `/api/oneclick/signup?sourceId=${createSourceId()}`
}

export function buildLoginHref() {
  return `/api/oneclick/signin?sourceId=${createSourceId()}`
}

function createSourceId() {
  return Math.random().toString(36).slice(2)
}

function toArray(value) {
  return Array.isArray(value) ? value : []
}

export function getModelContentMetadata(model) {
  const avatarUrl = model?.avatar_url ?? model?.avatarUrl ?? null
  const clickUrl = model?.click_url ?? model?.clickUrl ?? null
  const isNew = Boolean(model?.is_new ?? model?.isNew)
  const contentFavoritedCount = normalizeCount(model?.content_favorited_count ?? model?.contentFavoritedCount)
  const albums = toArray(model?.albums)
  const videos = toArray(model?.videos)

  return {
    avatarUrl,
    clickUrl,
    isNew,
    contentFavoritedCount,
    albums,
    videos,
  }
}

export function collectPhotoItems(contentItems = [], albums = []) {
  const structuredAlbums = toArray(albums)

  if (structuredAlbums.length > 0) {
    return structuredAlbums.flatMap((album) => {
      const albumId = album?.id ?? ''
      const albumTitle = album?.title ?? ''
      return toArray(album?.photos).map((photo) => ({
        id: photo?.id,
        type: 'photo',
        url: photo?.url,
        title: albumTitle || photo?.title,
        album_id: albumId,
        album_title: albumTitle,
        source: 'album_photo',
      }))
    })
  }

  // Legacy fallback: use flattened items if structured albums are missing.
  return contentItems.filter((item) => {
    const type = normalizeContentType(item?.type || item?.source)
    return type === 'photo' || type === 'image' || type === 'img'
  })
}

export function collectVideoItems(contentItems = [], videos = []) {
  const structuredVideos = toArray(videos)
    .filter((video) => (video?.url || '').trim())
    .map((video) => ({
      id: video?.id,
      type: 'video',
      title: video?.title,
      url: video?.url,
      thumbnail_url: video?.thumbnail_url ?? video?.thumbnailUrl ?? video?.thumbnail,
      thumbnailUrl: video?.thumbnail_url ?? video?.thumbnailUrl ?? video?.thumbnail,
      source: 'video',
    }))

  if (structuredVideos.length > 0) {
    return structuredVideos
  }

  // Legacy fallback: use flattened items only when structured videos are missing.
  return contentItems.filter((item) => normalizeContentType(item?.type || item?.source) === 'video')
}
